import { cidsService, type CidItem } from '@/services';
import { useQuery } from '@tanstack/react-query';

/**
 * Resolve um ÚNICO código de CID já salvo (ex.: `F41.2`) no seu item completo
 * (com `locator`/descrição). Usado na EDIÇÃO de uma vida: o código vem do
 * store/InsureMO sem ter passado por busca, então o combobox só teria o código
 * — este hook busca a descrição para exibir `F41.2 - <descrição>`.
 *
 * @param code código do CID (ex.: `F41.2`). Habilita só com >= 2 chars.
 */
export function useCidLabel(code: string | undefined) {
  const term = (code ?? '').trim();
  return useQuery<CidItem | null>({
    queryKey: ['services', 'cids', 'by-code', term.toUpperCase()],
    queryFn: async () => {
      const response = await cidsService.search({ q: term, page: 1, pageSize: 20 });
      if (!response.success || !response.data) return null;
      const items = response.data.items;
      // Match exato pelo código; senão, primeiro resultado como fallback.
      return (
        items.find(i => i.code.toUpperCase() === term.toUpperCase()) ??
        items[0] ??
        null
      );
    },
    enabled: term.length >= 2,
    retry: false,
    // Catálogo estático — segura por 1h (o back já cacheia 24h).
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Busca de CIDs (CID-10) na Public-Data API, com cache no React Query.
 * Server-side search (o catálogo é grande) — o termo já deve chegar
 * debounced pelo caller (o CidCombobox faz o debounce).
 *
 * Termo vazio carrega o catálogo inicial (>= 30 CIDs) para pré-popular o
 * combo ao abrir; 1 caractere é ruído e não dispara busca; >= 2 busca.
 *
 * @param q termo de busca (descrição ou código).
 */
const INITIAL_PAGE_SIZE = 30;

export function useCidSearch(q: string | undefined) {
  const term = (q ?? '').trim();
  return useQuery<CidItem[]>({
    queryKey: ['services', 'cids', 'search', term.toLowerCase()],
    queryFn: async () => {
      if (term.length === 1) return [];
      const response = await cidsService.search({
        q: term,
        page: 1,
        // Sem termo, traz uma página inicial maior pra popular o combo.
        pageSize: term.length === 0 ? INITIAL_PAGE_SIZE : 50,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error ?? 'Erro ao consultar CIDs');
      }
      return response.data.items;
    },
    enabled: term.length === 0 || term.length >= 2,
    retry: false,
    // Catálogo de CID-10 é estático; o back cacheia 24h. Aqui seguramos por
    // 1h para não repetir a mesma busca quando o corretor reabre o modal.
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
